import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc } from '@usacsmideo/types';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';

import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { FieldInfo } from '../../../../components/FieldInfo';
export const Route = createFileRoute('/_authenticated/$patientId/careTeam/attorneyInfo')({
  component: AttorneyInfoPage,
});
import * as Sentry from '@sentry/browser';

import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';

function AttorneyInfoPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  // REFS TO PREVENT RACE CONDITIONS DURING MUTATIONS
  const isSubmittingRef = React.useRef(false);
  const capturedFormValuesRef = React.useRef<typeof attorneyInfoForm.state.values | null>(null);

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // INITIALIZE FORM
  const attorneyInfoForm = useForm({
    defaultValues: {
      attorney: chart?.DOCTOR_INFO?.attorney ? chart?.DOCTOR_INFO?.attorney : '',
      attorneyStreetAddress: chart?.DOCTOR_INFO?.attorneyStreetAddress ? chart?.DOCTOR_INFO?.attorneyStreetAddress : '',
      attorneyCity: chart?.DOCTOR_INFO?.attorneyCity ? chart?.DOCTOR_INFO?.attorneyCity : '',
      attorneyState: chart?.DOCTOR_INFO?.attorneyState ? chart?.DOCTOR_INFO?.attorneyState : '',
      attorneyZIP: chart?.DOCTOR_INFO?.attorneyZIP ? chart?.DOCTOR_INFO?.attorneyZIP : '',
      attorneyCountry: chart?.DOCTOR_INFO?.attorneyCountry ? chart?.DOCTOR_INFO?.attorneyCountry : '',
      attorneyPhone: chart?.DOCTOR_INFO?.attorneyPhone ? chart?.DOCTOR_INFO?.attorneyPhone : '',
    },
    onSubmit: () => {
      handleDrInfoUpdate();
    },
  });

  // RESET FORM VALUES WHEN CHART DATA ARRIVES
  // THE useForm HOOK ONLY EVALUATES defaultValues ONCE ON MOUNT,
  // BUT chart IS INITIALLY UNDEFINED UNTIL THE SNAPSHOT ARRIVES
  // SKIP RESET IF WE'RE IN THE MIDDLE OF A MUTATION TO PREVENT RACE CONDITIONS
  React.useEffect(() => {
    if (isSubmittingRef.current) {
      // Skip form reset while mutation is in progress
      return;
    }
    if (chart?.DOCTOR_INFO) {
      const doctorInfo = chart.DOCTOR_INFO;
      attorneyInfoForm.setFieldValue('attorney', doctorInfo.attorney || '');
      attorneyInfoForm.setFieldValue('attorneyStreetAddress', doctorInfo.attorneyStreetAddress || '');
      attorneyInfoForm.setFieldValue('attorneyCity', doctorInfo.attorneyCity || '');
      attorneyInfoForm.setFieldValue('attorneyState', doctorInfo.attorneyState || '');
      attorneyInfoForm.setFieldValue('attorneyZIP', doctorInfo.attorneyZIP || '');
      attorneyInfoForm.setFieldValue('attorneyCountry', doctorInfo.attorneyCountry || '');
      attorneyInfoForm.setFieldValue('attorneyPhone', doctorInfo.attorneyPhone || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.DOCTOR_INFO]);

  // WRITE FORM DATA TO FIRESTORE
  const handleDrInfoUpdate = () => {
    // CAPTURE FORM VALUES AT SUBMIT TIME TO PREVENT RACE CONDITIONS
    isSubmittingRef.current = true;
    capturedFormValuesRef.current = { ...attorneyInfoForm.state.values };
    const captured = capturedFormValuesRef.current;

    mutate(
      {
        patientId: patientId,
        docData: {
          DOCTOR_INFO: {
            attorney: captured.attorney,
            attorneyStreetAddress: captured.attorneyStreetAddress,
            attorneyCity: captured.attorneyCity,
            attorneyState: captured.attorneyState,
            attorneyZIP: captured.attorneyZIP,
            attorneyCountry: captured.attorneyCountry,
            attorneyPhone: captured.attorneyPhone,
          },
        },
      },
      {
        onSuccess: () => {
          isSubmittingRef.current = false;
          // alert('Successfully updated document');
          setSubmitted(true);
        },
        onError: (error) => {
          isSubmittingRef.current = false;
          Sentry.captureException(error); // CAPTURE ERROR
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col">
          <h1 className="place-self-center text-3xl font-bold">ATTORNEY INFORMATION</h1>
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  attorneyInfoForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <attorneyInfoForm.Field
                    name="attorney"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length < 5) {
                          return 'Attorney name should be more than five characters';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Attorney Name*"
                          id="attorney-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          // required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <attorneyInfoForm.Field
                    name="attorneyStreetAddress"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length < 5) {
                          return 'Attorney street address should be more than five characters';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Attorney Street Address*"
                          id="attorney-street-address"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <attorneyInfoForm.Field
                    name="attorneyCity"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Attorney city cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Attorney City*"
                          id="attorney-city"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <attorneyInfoForm.Field
                    name="attorneyState"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length == 0) {
                          return 'Attorney state cannot be empty.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Attorney State*"
                          id="attorney-state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <attorneyInfoForm.Field
                    name="attorneyZIP"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length == 0) {
                          return 'Attorney ZIP Code cannot be empty.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Attorney ZIP Code*"
                          id="attorney-ZIP"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <attorneyInfoForm.Field
                    name="attorneyCountry"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length == 0) {
                          return 'Attorney country cannot be empty.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Attorney Country*"
                          id="attorney-country"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <h2 className="text-xl font-bold">Attorney Phone (Optional)</h2>
                  <attorneyInfoForm.Field
                    name="attorneyPhone"
                    children={(field) => (
                      <>
                        <PhoneInput
                          international
                          defaultCountry="US"
                          placeholder="Enter phone number"
                          value={field.state.value}
                          onChange={(value: string | undefined) => field.handleChange(value || '')}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <div className="mt-8 flex flex-col items-center">
                  <attorneyInfoForm.Subscribe
                    selector={(state) => [state.canSubmit, state.errors]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-hca-info"
                          type="submit"
                          disabled={!canSubmit}
                          onClick={attorneyInfoForm.handleSubmit}
                          textContent="Submit"
                          submitted={submitted}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {submitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
